// The Diagnosis and Action stage bodies under the Advisory strip (polish.P28b,
// #715). Locked comp: `.impeccable/mocks/decision/advisory-stages.html`,
// family A with no new comp (adjudication 2): Diagnosis puts the candidates in
// the 8 and the investigation runs in the 4; Action puts the cases in the 8 and
// the gate queue in the 4. Every derivation lives in
// `MidStageBodies.transform.ts`; the recipes are `stage-body-ui.ts`.

import { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { SurfacePagePanel, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { RecommendedAction } from '@/versions/v1/schema/case';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';
import type { InvestigationRunRow } from './InvestigationRun.transform';
import { InvestigationRunsPanel } from './InvestigationRunsPanel';
import type { FlowCaseRow, FlowLandingRead } from './FlowLanding.transform';
import { ActorChip } from './PipelineStrip';
import { diagnosisCandidateRows, gateQueueRows } from './MidStageBodies.transform';
import {
  STAGE_FEEDS,
  STAGE_MONEY,
  STAGE_MONEY_COMPARISON,
  STAGE_ROW_BUTTON,
  STAGE_ROW_META,
  STAGE_ROW_TITLE,
  STAGE_ROWS,
  STAGE_TABLE,
  STAGE_TD,
  STAGE_TD_FIRST,
  STAGE_TH,
  STAGE_TH_FIRST,
  STAGE_THEAD,
} from './stage-body-ui';

/** A list taller than the comp's 360 px body scrolls inside its card (as on P28a). */
const STAGE_LIST_SCROLL = 'max-h-[25rem] overflow-auto';

/** Keeps the one-off pair's `planned stop` phrase whole when its cell wraps
 *  narrow (K18 r1). */
export function NoBreakPlannedStop({ text }: { text: string }) {
  const phrase = 'planned stop';
  const at = text.indexOf(phrase);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="whitespace-nowrap">{phrase}</span>
      {text.slice(at + phrase.length)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Diagnosis
// ---------------------------------------------------------------------------

export function DiagnosisBody({
  read,
  runs,
  projectId,
  onOpenCase,
}: {
  read: Pick<FlowLandingRead, 'diagnosisRows' | 'openCaseRows'>;
  runs: readonly InvestigationRunRow[];
  projectId: string;
  onOpenCase: (caseId: string) => void;
}) {
  const rows = useMemo(() => diagnosisCandidateRows(read), [read]);
  // Absence renders nothing (DESIGN.md § Empty and waiting states): with no
  // case at Diagnosis at the cursor the candidates card is omitted and the
  // runs take the row; on the replay, while a case sits here, the row is 8/4.
  const runsPanel = runs.length > 0 ? (
    <InvestigationRunsPanel
      runs={runs}
      projectId={projectId}
      layout={rows.length > 0 ? 'rows' : 'table'}
      className="h-full"
    />
  ) : null;
  if (rows.length === 0) return runsPanel;
  return (
    <div className="grid grid-cols-12 gap-3">
      <div className={cn('col-span-12', runsPanel && 'lg:col-span-8')}>
        <Commentable name="Candidates">
          <SurfacePagePanel title="Top candidate cause" flush className="h-full">
            <div className={STAGE_LIST_SCROLL}>
              <Table clickableRows wrap={false} className={STAGE_TABLE}>
                <THead className={STAGE_THEAD}>
                  <TR>
                    <TH className={STAGE_TH_FIRST}>Case · age</TH>
                    <TH className={STAGE_TH}>Circuit</TH>
                    <TH className={cn(STAGE_TH, 'pr-4')}>Leading candidate</TH>
                  </TR>
                </THead>
                <TBody data-stage-body-list="diagnosis">
                  {rows.map((row) => (
                    <TR key={row.caseId} data-diagnosis-row={row.caseId} onRowClick={() => onOpenCase(row.caseId)}>
                      <TD className={cn(STAGE_TD_FIRST, 'min-w-52')}>
                        <div className={STAGE_ROW_TITLE}>{row.title}</div>
                        <div className={STAGE_ROW_META}>{row.meta}</div>
                      </TD>
                      <TD className={cn(STAGE_TD, 'whitespace-nowrap text-xs text-ink-secondary')}>{row.circuit}</TD>
                      <TD className={cn(STAGE_TD, 'whitespace-normal pr-4 text-xs leading-snug', row.zeroTouch ? 'text-muted' : 'text-ink-secondary')}>
                        {row.candidate}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </SurfacePagePanel>
        </Commentable>
      </div>
      {runsPanel ? <div className="col-span-12 lg:col-span-4">{runsPanel}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action
// ---------------------------------------------------------------------------

function ActionValueCell({ row, showMoney }: { row: FlowCaseRow; showMoney: boolean }) {
  if (showMoney && row.money) {
    return (
      <>
        <div className={STAGE_MONEY}>{row.money}</div>
        <div className={STAGE_MONEY_COMPARISON}><NoBreakPlannedStop text={row.moneyComparison} /></div>
      </>
    );
  }
  return <div className={STAGE_FEEDS}>{`Feeds ${row.feedLabel}`}</div>;
}

export function ActionBody({
  read,
  actions,
  showMoney,
  onOpenCase,
}: {
  read: Pick<FlowLandingRead, 'actionCaseRows'>;
  actions: readonly RecommendedAction[];
  /** Comp adjudication 7: the floor personas read the value path, never US$. */
  showMoney: boolean;
  onOpenCase: (caseId: string) => void;
}) {
  const gateRows = useMemo(() => gateQueueRows(read.actionCaseRows, actions), [read.actionCaseRows, actions]);
  return (
    <div className="grid grid-cols-12 gap-3">
      <div className={cn('col-span-12', gateRows.length > 0 && 'lg:col-span-8')}>
        <Commentable name="Actions">
          <SurfacePagePanel
            title="Cases in Action"
            meta={showMoney ? 'priced cases first, then unpriced by severity' : undefined}
            flush
            className="h-full"
          >
            <div className={STAGE_LIST_SCROLL}>
              <Table clickableRows wrap={false} className={STAGE_TABLE}>
                <THead className={STAGE_THEAD}>
                  <TR>
                    <TH className={STAGE_TH_FIRST}>Case · status</TH>
                    <TH className={STAGE_TH}>Who acts</TH>
                    <TH className={cn(STAGE_TH, 'pr-4 text-right')}>{showMoney ? `${CURRENCY_GLYPH}/day · value path` : 'Value path'}</TH>
                  </TR>
                </THead>
                <TBody data-stage-body-list="action">
                  {read.actionCaseRows.map((row) => (
                    <TR key={row.caseItem.id} data-action-row={row.caseItem.id} onRowClick={() => onOpenCase(row.caseItem.id)}>
                      <TD className={cn(STAGE_TD_FIRST, 'min-w-48')}>
                        <div className={STAGE_ROW_TITLE}>{row.caseItem.title}</div>
                        <div className={STAGE_ROW_META}>{row.caseItem.code ?? row.caseItem.circuit} · {row.actionStatus}</div>
                      </TD>
                      <TD className={cn(STAGE_TD, 'whitespace-nowrap')}><ActorChip actor="Human">{row.actor}</ActorChip></TD>
                      <TD className={cn(STAGE_TD, 'w-60 min-w-44 whitespace-normal pr-4 text-right tabular-nums')}>
                        <ActionValueCell row={row} showMoney={showMoney} />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </SurfacePagePanel>
        </Commentable>
      </div>
      {gateRows.length > 0 ? (
        <div className="col-span-12 lg:col-span-4">
          <Commentable name="GateQueue">
            <SurfacePagePanel title="Gate queue" flush className="h-full">
              <ul data-gate-queue className={cn(STAGE_LIST_SCROLL, STAGE_ROWS)}>
                {gateRows.map((row) => (
                  <li key={row.caseId} data-gate-row={row.caseId}>
                    <button
                      type="button"
                      onClick={() => onOpenCase(row.caseId)}
                      className={cn(STAGE_ROW_BUTTON, 'flex min-w-0 items-center gap-2 px-4 py-2.5')}
                    >
                      <span className="min-w-0 flex-1">
                        <span className={cn(STAGE_ROW_TITLE, 'block truncate')}>{row.title}</span>
                        <span className="mt-1 flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1">
                          <span className={cn(STAGE_ROW_META, 'mt-0')}>{row.code} · {row.status}</span>
                          <span className="shrink-0"><ActorChip actor="Human">{row.seat}</ActorChip></span>
                        </span>
                        {row.proposal ? (
                          <span className="mt-1 block line-clamp-2 text-xs leading-snug text-ink-secondary">{row.proposal}</span>
                        ) : null}
                      </span>
                      <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </SurfacePagePanel>
          </Commentable>
        </div>
      ) : null}
    </div>
  );
}
